import React, { useState } from 'react';
import { useScoreboard } from './context/ScoreboardContext';
import { useMedia } from './context/MediaContext';
import { useMusic } from './context/MusicContext';
import { useLanguage } from './context/LanguageContext';

const EntertainmentSettings = () => {
  const { 
    showEntertainmentSettings,
    setShowEntertainmentSettings,
    isEntertainmentEnabled,
  } = useScoreboard();

  const { 
    songs, 
    handleSongUpload, 
    removeSong, 
    youtubeVideos, 
    setYoutubeVideos, 
    youtubePlaylistUrl, 
    setYoutubePlaylistUrl 
  } = useMedia();

  const { musicVolume, setMusicVolume } = useMusic();
  const { t } = useLanguage();

  if (!showEntertainmentSettings) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-8 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">{t('entertainmentSettings.title')}</h1>
          <button
            onClick={() => setShowEntertainmentSettings(false)}
            className="text-gray-500 hover:text-gray-700"
          >
            ✕
          </button>
        </div>

        <div className="space-y-6">

          {/* Video Settings */}
          {isEntertainmentEnabled && (
            <div className="mt-8">
              <h2 className="text-lg font-bold mb-4">{t('entertainmentSettings.preGameVideos')}</h2>
              <div className="space-y-6">
                <div className="space-y-2 mb-4">
                  <label className="block text-sm font-medium text-gray-700">
                    {t('entertainmentSettings.youtubeMusicPlaylist')}
                  </label>
                  <input
                    type="text"
                    placeholder={t('entertainmentSettings.playlistPlaceholder')}
                    className="border rounded px-2 py-1 text-sm w-full"
                    value={youtubePlaylistUrl}
                    onChange={(e) => setYoutubePlaylistUrl(e.target.value)}
                  />
                  <p className="text-sm text-gray-500">
                    {t('entertainmentSettings.playlistDescription')}
                  </p>
                </div>

                {youtubeVideos.map((video, index) => (
                  <div key={index} className="space-y-2">
                    <div className="flex items-center gap-2">
                      <label className="block text-sm font-medium text-gray-700">
                        {t('entertainmentSettings.video')} {index + 1}
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={video.loop}
                          onChange={(e) => {
                            const newVideos = [...youtubeVideos];
                            newVideos[index] = { ...video, loop: e.target.checked };
                            setYoutubeVideos(newVideos);
                          }}
                          className="rounded border-gray-300 text-blue-600"
                        />
                        <span className="text-sm text-gray-600">{t('entertainmentSettings.loopVideo')}</span>
                      </div>
                    </div>
                    <input
                      type="text"
                      placeholder={t('entertainmentSettings.videoPlaceholder')}
                      className="border rounded px-2 py-1 text-sm w-full"
                      value={video.url}
                      onChange={(e) => {
                        const newVideos = [...youtubeVideos];
                        newVideos[index] = { ...video, url: e.target.value };
                        setYoutubeVideos(newVideos);
                      }}
                    />
                  </div>
                ))}
                <p className="text-sm text-gray-500">
                  {t('entertainmentSettings.videoDescription')}
                </p>
              </div>
            </div>
          )}

          {/* Music Management */}
          {isEntertainmentEnabled && (
            <div className="mt-8">
              <h2 className="text-lg font-bold mb-4">{t('entertainmentSettings.inGameTitle')}</h2>
              <div className="space-y-4">
                <p className="text-sm text-gray-600">{t('entertainmentSettings.inGameDescription')}</p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {songs.map((song, index) => (
                    <div key={index} className="border rounded-lg p-4 space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">{t('entertainmentSettings.songSlot')} {index + 1}</span>
                        {song && (
                          <button
                            onClick={() => removeSong(index)}
                            className="text-red-600 hover:text-red-700 text-sm"
                          >
                            {t('settings.remove')}
                          </button>
                        )}
                      </div>

                      {song && song.name ? (
                        <div className="text-sm text-gray-600 truncate">{song.name}</div>
                      ) : (
                        <div className="flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg p-4">
                          <label className="cursor-pointer text-center">
                            <span className="text-sm text-gray-500">{t('entertainmentSettings.clickToUploadMp3')}</span>
                            <input
                              type="file"
                              accept="audio/mp3,audio/mpeg"
                              className="hidden"
                              onChange={(e) => {
                                if (e.target.files?.[0]) {
                                  handleSongUpload(index, e.target.files[0]);
                                }
                              }}
                            />
                          </label>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EntertainmentSettings;
